import {
  runCreateWorkspaceWorkflow,
  runEditWorkspaceWorkflow,
  runMoveWorkspaceToTrashWorkflow,
  runRenameCanvasWorkflow,
  runShareCanvasWorkflow,
  runShareWorkspaceWorkflow,
} from "@/action-dialog";
import { restoreErrorMessage, useRestoreCanvas } from "@/api/archived";
import {
  useCreateCanvas,
  useDeleteCanvas,
  useWorkspaceSidebarData,
} from "@/api/canvas";
import { useCreateFlowSession } from "@/api/flow_sessions";
import { useSetCanvasFolder, useSetFlowSessionFolder } from "@/api/folders";
import { useCheckBulkPermissions } from "@/api/relations";
import {
  useFavoriteCanvases,
  useFavoriteFileRecords,
} from "@/api/resourceTags";
import { useUserInfo } from "@/api/user";
import { useUsers } from "@/api/users";
import {
  useCreateWorkspace,
  useDeleteWorkspace,
  usePrivateWorkspace,
  useUpdateWorkspace,
} from "@/api/workspaces";
import { showMovedToTrashToast } from "@/components/ConfirmTrashDialog/movedToTrashToast";
import { ActionRow } from "@/components/rail-kit/ActionRow";
import { Accordion } from "@/components/ui/accordion";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { ROUTES } from "@/constants/routes";
import { RailAllFavoritesDropTarget } from "@/dnd";
import { useOpenChat } from "@/hooks/useOpenChat";
import { FilesRailDraggableFileRow } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/FilesRailDraggableFileRow";
import { RailWorkspaceExpandableRow } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailExpandableRow";
import { RailGroup } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailGroup";
import { RailMultiSelectionMenuProvider } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailMultiSelectionMenu";
import {
  RailRowMenu,
  railRowMenuProps,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailRowMenu";
import { RailSelectionBar } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailSelectionBar";
import { RAIL_SELECTION_ROOT_ATTR } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailSelectionContext";
import { RailZeroState } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailZeroState";
import { railZeroStateCopy } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/railZeroStateCopy";
import {
  useRailExpansion,
  useRailExpansionGroups,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/useRailExpansion";
import { useRailRowContextMenu } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/useRailRowContextMenu";
import { useWorkspacesRailMultiSelectionActions } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/workspaces/useWorkspacesRailMultiSelectionActions";
import { WorkspacesRailCanvasNode } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/workspaces/WorkspacesRailCanvasNode";
import { WorkspacesRailWorkspaceRow } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/workspaces/WorkspacesRailWorkspaceRow";
import {
  useActiveFileRecordId,
  useOpenFileRecord,
} from "@/panes/file-record-detail";
import { route } from "@/utils/route";
import {
  FLOW_SESSION_MODES,
  RESOURCE_TAG_FAVORITE,
  type Canvas,
  type OrganizationUser,
  type Workspace,
} from "@cohesive/api-client";
import { Layers, Plus, Star, User, Users } from "lucide-react";
import { useCallback, useMemo, type ReactNode } from "react";
import toast from "react-hot-toast";
import { useLocation, useMatch, useNavigate } from "react-router-dom";

const RAIL_GROUP_IDS = [
  "workspaces-favorites",
  "workspaces-all",
  "workspaces-shared",
] as const;
// Favorites starts collapsed like Shared with me (designer ruling, Aug
// 2026) — a known concept doesn't need to open at rest. Saved expansion
// state still wins for returning sessions.
const RAIL_GROUP_DEFAULT_OPEN = ["workspaces-all"];

export function WorkspacesRail() {
  const { data, isError } = useWorkspaceSidebarData();
  const { data: userInfo } = useUserInfo();
  const { data: orgUsers } = useUsers();
  const privateWorkspace = usePrivateWorkspace();
  const navigate = useNavigate();
  const { openChat } = useOpenChat();
  const { pathname } = useLocation();
  const activeCanvasMatch = useMatch(ROUTES.CANVAS.DETAIL);
  const activeCanvasId = activeCanvasMatch?.params?.canvasId ?? null;

  const newWorkspaceMenu = useRailRowContextMenu();
  const createWorkspace = useCreateWorkspace();
  const [sharedOpen, setSharedOpen] = useRailExpansion("rail:shared-with-me");
  const [openGroups, setOpenGroups] = useRailExpansionGroups(
    RAIL_GROUP_IDS,
    RAIL_GROUP_DEFAULT_OPEN,
  );
  const {
    selectionCount,
    selectionBarActions,
    leftRailMultiActions,
    clear: clearSelection,
    clearIfOutside,
  } = useWorkspacesRailMultiSelectionActions();

  // Consolidated Favorites: favorite canvases, chats, and files across all
  // workspaces — canvases and files, both from the ?tag=favorite lists.
  const { data: favoriteCanvases = [] } = useFavoriteCanvases();
  const { data: favoriteFiles = [] } = useFavoriteFileRecords();
  const openFileRecord = useOpenFileRecord();
  const activeFileRecordId = useActiveFileRecordId();
  const setCanvasFolder = useSetCanvasFolder();
  const setFlowSessionFolder = useSetFlowSessionFolder();

  const sidebarTeamWorkspaces = useMemo(
    () => (data?.workspaces ?? []).filter((ws) => !ws.is_private),
    [data?.workspaces],
  );

  const workspaceDeleteChecks = useMemo(
    () =>
      sidebarTeamWorkspaces.map((ws) => ({
        resource_object_type: "workspace" as const,
        resource_object_id: ws.id,
        permission: "can_delete" as const,
      })),
    [sidebarTeamWorkspaces],
  );

  const { data: workspacePermissions } = useCheckBulkPermissions({
    checks: workspaceDeleteChecks,
  });

  const deleteWorkspace = useDeleteWorkspace({
    onError: () => toast.error("Failed to delete Workspace"),
  });

  const updateWorkspace = useUpdateWorkspace({
    onError: () => toast.error("Failed to update workspace"),
  });

  const createCanvas = useCreateCanvas({
    onError: () => toast.error("Failed to create canvas"),
  });

  const deleteCanvas = useDeleteCanvas({
    onError: () => toast.error("Failed to delete canvas"),
  });
  const restoreCanvas = useRestoreCanvas({
    onError: (err) =>
      toast.error(restoreErrorMessage(err, "Failed to restore canvas")),
  });

  const createFlowSession = useCreateFlowSession({
    onError: () => toast.error("Failed to create chat"),
  });

  const handleNewCanvas = useCallback(
    (workspaceId: string, folderId?: string) => {
      if (!workspaceId || createCanvas.isPending) return;
      createCanvas.mutate(
        { workspaceId },
        {
          onSuccess: (newCanvas) => {
            // Created from a folder's menu → file it into that folder. The
            // canvas exists either way, so a filing failure only means it
            // landed at the workspace root — say so instead of failing silently.
            if (folderId) {
              setCanvasFolder.mutate(
                {
                  canvasId: newCanvas.id,
                  folderId,
                  workspaceId,
                },
                {
                  onError: () =>
                    toast.error(
                      "Canvas created at the workspace root — couldn't file it into the folder.",
                    ),
                },
              );
            }
            navigate(route(ROUTES.CANVAS.DETAIL, { canvasId: newCanvas.id }));
          },
        },
      );
    },
    [createCanvas, navigate, setCanvasFolder],
  );

  const handleNewChat = useCallback(
    (workspaceId: string, folderId?: string) => {
      if (!workspaceId || createFlowSession.isPending) return;
      createFlowSession.mutate(
        {
          mode: FLOW_SESSION_MODES.WORKSPACE_CHAT,
          class: "workspace",
          workspace_id: workspaceId,
        },
        {
          onSuccess: (session) => {
            // Created from a folder's menu → file it into that chat folder.
            // The chat exists either way; surface a filing failure.
            if (folderId) {
              setFlowSessionFolder.mutate(
                {
                  flowSessionId: session.id,
                  folderId,
                  workspaceId,
                },
                {
                  onError: () =>
                    toast.error(
                      "Chat created at the workspace root — couldn't file it into the folder.",
                    ),
                },
              );
            }
            openChat(session.id, { mode: "replace", source: "rail" });
          },
        },
      );
    },
    [createFlowSession, openChat, setFlowSessionFolder],
  );

  const handleRenameCanvas = useCallback((canvas: Canvas) => {
    window.setTimeout(() => void runRenameCanvasWorkflow({ canvas }), 0);
  }, []);

  const handleShareCanvas = useCallback((canvas: Canvas) => {
    window.setTimeout(() => void runShareCanvasWorkflow({ canvas }), 0);
  }, []);

  const activeWorkspaceId = pathname.startsWith("/workspaces/")
    ? pathname.split("/")[2]
    : null;

  const handleDeleteCanvas = useCallback(
    (canvas: Canvas) => {
      deleteCanvas.mutate(canvas.id, {
        onSuccess: () => {
          if (activeCanvasId === canvas.id) {
            navigate(ROUTES.CANVAS.ROOT);
          }
          showMovedToTrashToast(() => restoreCanvas.mutate(canvas.id));
        },
      });
    },
    [activeCanvasId, deleteCanvas, navigate, restoreCanvas],
  );

  const moveWorkspaceToTrash = useCallback(
    (workspace: Workspace) =>
      runMoveWorkspaceToTrashWorkflow({
        workspace,
        deleteWorkspace,
        onSuccess: (ws) => {
          if (activeWorkspaceId === ws.id) navigate("/");
        },
      }),
    [activeWorkspaceId, deleteWorkspace, navigate],
  );

  const editWorkspace = useCallback(
    (workspace: Workspace) =>
      runEditWorkspaceWorkflow({ workspace, updateWorkspace }),
    [updateWorkspace],
  );

  // No navigation on success — workspaces aren't destinations anymore; the new
  // workspace appears as a row in the rail.
  const openCreateWorkspace = useCallback(
    () => void runCreateWorkspaceWorkflow({ createWorkspace }),
    [createWorkspace],
  );

  const shareWorkspaceAction = useCallback(
    (workspace: Workspace) => void runShareWorkspaceWorkflow({ workspace }),
    [],
  );

  const currentUserId = userInfo?.user?.id;
  const usersById = useMemo(() => {
    const map = new Map<string, OrganizationUser>();
    for (const u of orgUsers ?? []) map.set(u.id, u);
    return map;
  }, [orgUsers]);

  const sharedCanvases = useMemo(
    () =>
      [...(data?.sharedWithMe ?? [])].sort(
        (a, b) =>
          new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
      ),
    [data?.sharedWithMe],
  );

  const flatRailCanvasPermissionChecks = useMemo(
    () =>
      [...sharedCanvases, ...favoriteCanvases].flatMap((canvas) => [
        {
          resource_object_type: "canvas" as const,
          resource_object_id: canvas.id,
          permission: "can_delete" as const,
        },
        {
          resource_object_type: "canvas" as const,
          resource_object_id: canvas.id,
          permission: "can_manage_assignments" as const,
        },
      ]),
    [sharedCanvases, favoriteCanvases],
  );

  const { data: flatRailCanvasPermissions } = useCheckBulkPermissions({
    checks: flatRailCanvasPermissionChecks,
  });

  const canDeleteFlatRailCanvas = useCallback(
    (canvasId: string) =>
      flatRailCanvasPermissions?.canvas?.[canvasId]?.can_delete ?? false,
    [flatRailCanvasPermissions],
  );

  const canShareFlatRailCanvas = useCallback(
    (canvasId: string) =>
      flatRailCanvasPermissions?.canvas?.[canvasId]?.can_manage_assignments ??
      false,
    [flatRailCanvasPermissions],
  );

  const teamWorkspaceRows = useMemo(
    () =>
      sidebarTeamWorkspaces.map((ws) => (
        <WorkspacesRailWorkspaceRow
          key={ws.id}
          workspace={ws}
          active={activeWorkspaceId === ws.id}
          activeCanvasId={activeCanvasId}
          currentUserId={currentUserId}
          usersById={usersById}
          onRename={() => void editWorkspace(ws)}
          onShare={() => shareWorkspaceAction(ws)}
          onDelete={
            workspacePermissions?.workspace?.[ws.id]?.can_delete
              ? () => void moveWorkspaceToTrash(ws)
              : undefined
          }
          onRenameCanvas={handleRenameCanvas}
          onShareCanvas={handleShareCanvas}
          onDeleteCanvas={handleDeleteCanvas}
          onNewCanvas={(folderId) => handleNewCanvas(ws.id, folderId)}
          onNewChat={(folderId) => handleNewChat(ws.id, folderId)}
          onOpen={() =>
            navigate(route(ROUTES.WORKSPACES.DETAIL, { workspaceId: ws.id }))
          }
        />
      )),
    [
      sidebarTeamWorkspaces,
      handleNewCanvas,
      handleNewChat,
      navigate,
      activeWorkspaceId,
      activeCanvasId,
      currentUserId,
      usersById,
      workspacePermissions,
      moveWorkspaceToTrash,
      editWorkspace,
      handleRenameCanvas,
      handleShareCanvas,
      handleDeleteCanvas,
    ],
  );

  const allWorkspaceItems = useMemo(() => {
    const items: ReactNode[] = [];
    if (privateWorkspace) {
      items.push(
        <WorkspacesRailWorkspaceRow
          key={privateWorkspace.id}
          workspace={privateWorkspace}
          labelOverride="My Workspace"
          icon={User}
          hideWorkspaceMenu
          defaultOpen
          active={activeWorkspaceId === privateWorkspace.id}
          activeCanvasId={activeCanvasId}
          currentUserId={currentUserId}
          usersById={usersById}
          onRename={() => {}}
          onShare={() => {}}
          onRenameCanvas={handleRenameCanvas}
          onShareCanvas={handleShareCanvas}
          onDeleteCanvas={handleDeleteCanvas}
          onNewCanvas={(folderId) =>
            handleNewCanvas(privateWorkspace.id, folderId)
          }
          onNewChat={(folderId) => handleNewChat(privateWorkspace.id, folderId)}
          onOpen={() =>
            navigate(
              route(ROUTES.WORKSPACES.DETAIL, {
                workspaceId: privateWorkspace.id,
              }),
            )
          }
          newCanvasDisabled={!privateWorkspace.id || createCanvas.isPending}
        />,
      );
    }
    items.push(...teamWorkspaceRows);
    return items;
  }, [
    privateWorkspace,
    teamWorkspaceRows,
    activeWorkspaceId,
    activeCanvasId,
    currentUserId,
    usersById,
    handleRenameCanvas,
    handleShareCanvas,
    handleDeleteCanvas,
    handleNewCanvas,
    handleNewChat,
    navigate,
    createCanvas.isPending,
  ]);

  if (isError) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center px-4 py-8">
        <p className="text-center text-sm text-n-40">
          Couldn&apos;t load workspaces.
        </p>
      </div>
    );
  }

  // Shared canvases expand to their files, same as canvases under a workspace.
  const sharedCanvasRows = sharedCanvases.map((canvas) => (
    <WorkspacesRailCanvasNode
      key={canvas.id}
      canvas={canvas}
      dragNamespace="shared"
      active={activeCanvasId === canvas.id}
      onRename={handleRenameCanvas}
      onShare={
        canShareFlatRailCanvas(canvas.id) ? handleShareCanvas : undefined
      }
      onDelete={
        canDeleteFlatRailCanvas(canvas.id) ? handleDeleteCanvas : undefined
      }
    />
  ));

  // The rail shows places only: files shared directly with me surface on the
  // Home shared-with-me view (D18), never as rail rows. Chats can't be
  // shared, so the section is canvases alone.
  const sharedRows = [...sharedCanvasRows];

  // Filter on tags (not just the fetched set) so an optimistic unstar drops the
  // row immediately, before the ?tag=favorite refetch lands — matching the
  // skills/connectors rails. `section="favorites"` keeps the highlight on the
  // row you clicked instead of also lighting up the canonical copy. Favorites
  // canvases expand to their files, same as everywhere else.
  const favoriteCanvasRows = favoriteCanvases
    .filter((canvas) => canvas.tags?.includes(RESOURCE_TAG_FAVORITE))
    .map((canvas) => (
      <WorkspacesRailCanvasNode
        key={canvas.id}
        canvas={canvas}
        dragNamespace="favorites"
        section="favorites"
        active={activeCanvasId === canvas.id}
        onRename={handleRenameCanvas}
        onShare={
          canShareFlatRailCanvas(canvas.id) ? handleShareCanvas : undefined
        }
        onDelete={
          canDeleteFlatRailCanvas(canvas.id) ? handleDeleteCanvas : undefined
        }
      />
    ));

  // File favorites — the ?tag=favorite workspace file list.
  const favoriteFileRows = favoriteFiles
    .filter((record) => record.tags?.includes(RESOURCE_TAG_FAVORITE))
    .map((record) => (
      <FilesRailDraggableFileRow
        key={`fav-file-${record.id}`}
        record={record}
        workspaceId={record.workspace_id ?? ""}
        dragNamespace="favorites"
        section="favorites"
        active={activeFileRecordId === record.id}
        onOpen={() => openFileRecord(record.id)}
        // Aligns with sibling canvas/chat rows, whose (hover-only) chevron
        // slot is always reserved.
        withChevronSpacer
      />
    ));

  // Canvases and files only. A chat is pinned, not favourited, and its pins
  // live in the Chats view's own Pinned group — carrying them here too would
  // put one chat in two places under two different verbs.
  const favoriteRows = [...favoriteCanvasRows, ...favoriteFileRows];

  return (
    <RailMultiSelectionMenuProvider actions={leftRailMultiActions}>
      {/* Positioning context for the floating selection bar, which overlays
          the top of the scroll area instead of pushing the rail down. */}
      <div className="relative flex min-h-0 flex-1 flex-col">
        <RailSelectionBar
          count={selectionCount}
          registryActions={selectionBarActions}
          onClose={clearSelection}
        />
        <div
          className="flex min-h-0 flex-1 flex-col overflow-y-auto px-2 pb-2 pt-3"
          {...{ [RAIL_SELECTION_ROOT_ATTR]: "" }}
          onClick={clearIfOutside}
        >
          <Accordion
            type="multiple"
            // Favorites + Workspaces are expanded by default; Shared starts
            // collapsed. The value persists (with the rest of the rail's
            // expansion state) so the rail comes back exactly as you left it.
            value={openGroups}
            onValueChange={setOpenGroups}
            // One seam between every top-level item — Favorites, Shared with
            // me, the workspaces — so the run reads evenly instead of the
            // sections drifting apart where a wrapper happened to add margin.
            className="flex flex-col gap-2"
          >
            <RailAllFavoritesDropTarget>
              <RailGroup
                groupId="workspaces-favorites"
                label="Favorites"
                icon={Star}
                items={favoriteRows}
                emptyState={
                  <RailZeroState
                    icon={Star}
                    accent="rust"
                    variant="passive"
                    title="Pin what matters."
                    description="Add a canvas, chat, or file to favorites and it stays here."
                  />
                }
              />
            </RailAllFavoritesDropTarget>

            {/* The workspaces themselves sit at the top level rather than
                inside a group of their own. Favorites and Shared with me are
                VIEWS — derived lenses that earn an eyebrow — while a
                workspace is a place you enter, and a place does not need a
                heading announcing that places exist. The run closes on its
                own create verb. */}
            <div
              // 4px between top-level places, against 2px between the rows
              // inside one. A workspace is a heavier thing than a canvas, so
              // it gets its own rung — still strictly monotonic, still on the
              // 4px grid, and small enough to read as breathing room rather
              // than a new seam.
              className="flex flex-col gap-1"
              onContextMenu={newWorkspaceMenu.onContextMenu}
            >
              {/* Shared with me is a CONTAINER, not a view: it holds places
                other people gave you, so it reads like the workspaces it sits
                above — sans label, icon tile — rather than wearing the mono
                eyebrow that marks a derived lens like Favorites. */}
              <RailWorkspaceExpandableRow
                icon={Users}
                label="Shared with me"
                open={sharedOpen}
                onOpenChange={setSharedOpen}
              >
                {sharedRows.length > 0 ? (
                  sharedRows
                ) : (
                  <RailZeroState
                    icon={Users}
                    accent="rust"
                    variant="passive"
                    title={railZeroStateCopy.workspaces.shared.title}
                    description={
                      railZeroStateCopy.workspaces.shared.description
                    }
                  />
                )}
              </RailWorkspaceExpandableRow>

              {allWorkspaceItems.length > 0 ? (
                allWorkspaceItems
              ) : (
                <RailZeroState
                  icon={Layers}
                  accent="rust"
                  title={railZeroStateCopy.workspaces.teams.title}
                  description={railZeroStateCopy.workspaces.teams.description}
                />
              )}
              <ActionRow
                icon={Plus}
                label="New workspace"
                onClick={() =>
                  window.setTimeout(() => openCreateWorkspace(), 0)
                }
              />
            </div>
          </Accordion>
        </div>
      </div>

      <RailRowMenu
        {...railRowMenuProps(newWorkspaceMenu)}
        kebab={false}
        label="Workspaces"
      >
        <DropdownMenuItem
          onClick={() => window.setTimeout(() => openCreateWorkspace(), 0)}
        >
          <Plus />
          New Workspace
        </DropdownMenuItem>
      </RailRowMenu>
    </RailMultiSelectionMenuProvider>
  );
}
